import React, { useImperativeHandle, forwardRef, useState, useEffect, useMemo, useRef, } from 'react';
import Rte, { EditorValue, decorator, getTextAlignClassName, getTextAlignStyles, getTextAlignBlockMetadata, } from 'react-rte';
import { isEmpty, map, } from 'lodash';
import { CompositeDecorator, EditorState, Modifier, convertToRaw, } from 'draft-js';
import { Button, Input, } from 'reactstrap';
import { ENTITY_TYPE } from 'draft-js-utils';
import { useToggle } from 'react-use';
import moviePlayImage from '../images/movie_play.png';

import { storageRef, uploadBytes, getDownloadURL } from '../firebase';
import { serial, } from '../shared/util';
import { resizeImage } from '../util';
import { isURL } from 'validator';
import ModelFormModal from './modals/ModelFormModal';
import ModalButton from './ModalButton';
import IconDecorator, { ICON_DATA_KEY, iconSizes, iconSizePx, } from './rte/IconDecorator';
import IconLibrary, { rteIconsRef } from './rte/IconLibrary';
import useTenant from './hooks/useTenant';

// react-rte 既定の [LinkDecorator, ImageDecorator] の手前に IconDecorator を差し込む。
// CompositeDecorator は配列順で先勝ちなので、data-icon を持つ画像だけが IconDecorator に、
// 通常画像は従来どおり ImageDecorator に落ちる。
const iconAwareDecorator = new CompositeDecorator([IconDecorator, ...decorator._decorators]);

const createValue = (html) => EditorValue.createFromString(html || '', 'html', iconAwareDecorator, { customBlockFn: getTextAlignBlockMetadata, });

// PDF ボタンのアイコン。Font Awesome 5 無料版の fa-file-pdf は
// 一目で PDF と分からないので、書類の形 +「PDF」の文字を自前で描く。
// サイズ・色は他の Font Awesome ボタンに合わせて 1em / currentColor。
const PdfIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 384 512"
    width="1em"
    height="1em"
    fill="currentColor"
    style={{ verticalAlign: '-0.125em' }}
  >
    <path d="M224 0v128h128L224 0zM192 160V0H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V160H192z" />
    <text
      x="192"
      y="400"
      textAnchor="middle"
      fontSize="150"
      fontWeight="bold"
      fontFamily="Helvetica, Arial, sans-serif"
      fill="#fff"
    >
      PDF
    </text>
  </svg>
);

// アイコン挿入モーダルの上部タブ。タブ状態は親が持つ:
// アップロード欄の表示は「ライブラリタブを見ているか」で決まり、fields 側の hidden から参照する。
// モーダルは閉じると ModalButton にアンマウントされるので、状態もそこで破棄する。
const IconModalHeader = ({ isLibrary, libraryUrl, onChangeTab, onSelect, onUnmount }) => {
  useEffect(() => onUnmount, []);

  return (
    <div className="mb-3">
      <ul className="nav nav-tabs mb-3">
        {
          [
            { key: 'upload', label: 'アップロード', isActive: !isLibrary },
            { key: 'library', label: 'ライブラリから選択', isActive: isLibrary },
          ].map(({ key, label, isActive }) => (
            <li key={key} className="nav-item">
              <button
                type="button"
                className={`nav-link btn btn-link ${isActive ? 'active' : ''}`}
                onClick={_ => onChangeTab(key === 'library')}
              >
                {label}
              </button>
            </li>
          ))
        }
      </ul>
      {isLibrary && <IconLibrary selectedUrl={libraryUrl} onSelect={onSelect} />}
    </div>
  );
};

export default forwardRef(function RichTextEditor(props, ref) {
  const { value, onChange, ...extraProps } = props;
  const rte = useRef();
  const tenant = useTenant();
  // ライブラリはテナント単位。TenantPage 外で描画された場合は tenant が null になるため、
  // その場合はライブラリタブを出さず従来どおりアップロードのみにする。
  const [iconTab, setIconTab] = useState({ isLibrary: false, libraryUrl: null });
  const [rteValue, setRteValue] = useState(() => createValue(value));
  const emptyString = useMemo(() => Rte.createEmptyValue().toString('html'), []);
  useImperativeHandle(ref, () => ({
    setRteValue: (value) => setRteValue(createValue(value)),
  }));
  useEffect(() => {
    const htmlString = rteValue.toString('html', { blockStyleFn: getTextAlignStyles, });

    onChange(htmlString === emptyString ? '' : htmlString);
  }, [rteValue, emptyString]);

  return (
    <div className="app-rte">
      <Rte
        className="vsm-rte"
        ref={rte}
        value={rteValue}
        onChange={setRteValue}
        toolbarConfig={{
          display: ['INLINE_STYLE_BUTTONS', 'BLOCK_ALIGNMENT_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS', 'IMAGE_BUTTON', 'BLOCK_TYPE_DROPDOWN', 'HISTORY_BUTTONS'],
          INLINE_STYLE_BUTTONS: [{ label: 'Bold', style: 'BOLD' }, { label: 'Italic', style: 'ITALIC' }, { label: 'Strikethrough', style: 'STRIKETHROUGH' }, { label: 'Monospace', style: 'CODE' }, { label: 'Underline', style: 'UNDERLINE' }],
          BLOCK_ALIGNMENT_BUTTONS: [{ label: 'Align Left', style: 'ALIGN_LEFT' }, { label: 'Align Center', style: 'ALIGN_CENTER' }, { label: 'Align Right', style: 'ALIGN_RIGHT' }, { label: 'Align Justify', style: 'ALIGN_JUSTIFY' }],
          BLOCK_TYPE_DROPDOWN: [
            { label: 'Normal', style: 'unstyled' },
            { label: 'Heading Large', style: 'header-one' },
            { label: 'Heading Medium', style: 'header-two' },
            { label: 'Heading Small', style: 'header-three' },
            { label: 'Code Block', style: 'code-block' },
            { label: '装飾1', style: 'header-four' },
            { label: '装飾2', style: 'header-five' },
          ],
          BLOCK_TYPE_BUTTONS: [{ label: 'UL', style: 'unordered-list-item' }, { label: 'OL', style: 'ordered-list-item' }, { label: 'Blockquote', style: 'blockquote' }],
        }}
        customControls={[
          ...(
            [
              { type: 'image', label: '画像', },
              { type: 'video', label: '動画', },
              { type: 'audio', label: '音声', },
              { type: 'icon', label: 'アイコン', },
              { type: 'pdf', label: 'PDF', },
            ].map(({ type, label }) => {
              return (setState, getState, editorState) => {
                const onSubmit = async (values, { onClickClose }) => {
                  const { url, file, poster, size, text, } = values;

                  let fileUrl, thumbnailUrl, posterUrl;

                  if (type === 'pdf' && file) {
                    // PDF はリサイズせずそのままアップロードし、表示テキストのリンクとして挿入する。
                    const timestamp = (new Date()).getTime().toString();
                    const pdfPath = ['rte', timestamp, file.name].join('/');
                    const pdfRef = storageRef(pdfPath);
                    await uploadBytes(pdfRef, file, { contentType: file.type });
                    fileUrl = await getDownloadURL(pdfRef);

                    let contentState = editorState.getCurrentContent();
                    const selection = editorState.getSelection();
                    // 表示テキストを挿入し、その範囲にリンクエンティティを適用する。
                    contentState = contentState.createEntity(ENTITY_TYPE.LINK, 'MUTABLE', { url: fileUrl, target: '_blank' });
                    const entityKey = contentState.getLastCreatedEntityKey();
                    const linkText = isEmpty(text) ? fileUrl : text;
                    const withText = Modifier.insertText(contentState, selection, linkText, null, entityKey);
                    rte.current._onChange(EditorState.push(editorState, withText));
                    await new Promise(_ => setTimeout(_, 10));
                    setTimeout(_ => rte.current.editor.focus(), 50);
                    onClickClose();
                    return;
                  }

                  if (type === 'icon' && iconTab.libraryUrl) {
                    // ライブラリから選択。アップロードも台帳への追加もせず URL を再利用する。
                    fileUrl = iconTab.libraryUrl;
                  } else if (type === 'icon' && file) {
                    const timestamp = (new Date()).getTime().toString();

                    // アイコンは小さいので縮小版のみアップロード
                    const resizedFile = await resizeImage(file, 200);
                    const iconPath = ['rte', timestamp, `icon_200_${file.name}`].join('/');
                    const iconRef = storageRef(iconPath);
                    await uploadBytes(iconRef, resizedFile, { contentType: file.type });
                    fileUrl = await getDownloadURL(iconRef);

                    // 次回以降ライブラリから選べるよう台帳に残す。
                    // 記事本文からアイコンを消しても台帳と Storage は残る(従来から同じ)。
                    if (tenant) {
                      await rteIconsRef.doc().set({
                        tenantId: tenant.id,
                        url: fileUrl,
                        fileName: file.name,
                        createdAt: new Date(),
                      });
                    }
                  } else if (type === 'image' && file) {
                    const timestamp = (new Date()).getTime().toString();

                    // 元画像をアップロード
                    const originalPath = ['rte', timestamp, file.name].join('/');
                    const originalRef = storageRef(originalPath);
                    await uploadBytes(originalRef, file, { contentType: file.type });
                    fileUrl = await getDownloadURL(originalRef);

                    // サムネイル（800px）をアップロード
                    const resizedFile = await resizeImage(file, 800);
                    const thumbPath = ['rte', timestamp, `thumb_800_${file.name}`].join('/');
                    const thumbRef = storageRef(thumbPath);
                    await uploadBytes(thumbRef, resizedFile, { contentType: file.type });
                    thumbnailUrl = await getDownloadURL(thumbRef);
                  } else {
                    // 動画・音声の場合は従来通り
                    const [_fileUrl, _posterUrl] = await serial([file, poster], async (file) => {
                      if(file == null) return;

                      const path = ['rte', (new Date()).getTime().toString(), file.name].join('/');
                      const fileRef = storageRef(path);
                      await uploadBytes(fileRef, file, { contentType: file.type });
                      const downloadUrl = await getDownloadURL(fileRef);
                      return downloadUrl;
                    });
                    fileUrl = _fileUrl;
                    posterUrl = _posterUrl;
                  }

                  let contentState = editorState.getCurrentContent();
                  const selection = editorState.getSelection();
                  contentState = contentState.createEntity(ENTITY_TYPE.IMAGE, 'IMMUTABLE', ({
                    image: {
                      src: thumbnailUrl || fileUrl,
                      alt: url || fileUrl,
                    },
                    video: {
                      src: posterUrl || moviePlayImage,
                      alt: `[video] ${fileUrl}`,
                    },
                    audio: {
                      src: moviePlayImage,
                      alt: `[audio] ${fileUrl}`,
                    },
                    // width/height は ImageSpan による実寸上書きを防ぐために持たせる。
                    // 表示側の sanitize では落とされ、サイズは data-icon 経由の CSS が決める。
                    icon: {
                      src: fileUrl,
                      alt: url || fileUrl,
                      [ICON_DATA_KEY]: size,
                      width: iconSizePx(size),
                      height: iconSizePx(size),
                    },
                  })[type]);
                  const entityKey = contentState.getLastCreatedEntityKey();
                  const newContentState = Modifier.insertText(contentState, selection, ' ', null, entityKey);
                  rte.current._onChange(EditorState.push(editorState, newContentState));
                  await new Promise(_ => setTimeout(_, 10));
                  setTimeout(_ => rte.current.editor.focus(), 50);
                  onClickClose();
                };

                // ライブラリはアイコンのみ。テナント外(tenant=null)では出さない。
                const hasLibrary = type === 'icon' && tenant != null;

                return (
                  <ModalButton
                    size="sm"
                    className="ml-1"
                    Modal={ModelFormModal}
                    modalProps={{
                      title: label,
                      renderFormHeader: hasLibrary ? (_ => (
                        <IconModalHeader
                          isLibrary={iconTab.isLibrary}
                          libraryUrl={iconTab.libraryUrl}
                          onChangeTab={_ => setIconTab({ isLibrary: _, libraryUrl: null })}
                          onSelect={_ => setIconTab({ isLibrary: true, libraryUrl: _ })}
                          onUnmount={_ => setIconTab({ isLibrary: false, libraryUrl: null })}
                        />
                      )) : undefined,
                      fields: {
                        file: {
                          label: `${label}ファイル`,
                          type: 'file',
                          inputProps: {
                            accept: { icon: 'image/*', pdf: 'application/pdf' }[type] || `${type}/*`,
                          },
                          // ライブラリタブを見ている間はアップロード欄を隠す。
                          // (file と libraryUrl の両方が立つと、どちらが挿入されるか読めなくなる)
                          hidden: _ => iconTab.isLibrary,
                        },
                        ...(
                          ['image', 'icon'].includes(type) && {
                            url: {
                              label: 'リンク先URL',
                              type: 'string',
                              validations: {
                                format: v => isEmpty(v) || v.includes('localhost') || isURL(v),
                              },
                            }
                          }
                        ),
                        ...(
                          type === 'icon' && {
                            size: {
                              label: 'サイズ',
                              type: 'select',
                              options: map(iconSizes, (_, key) => ({ label: `${_.label}（${_.px}px）`, value: key })),
                              initialValue: 'md',
                              validations: {
                                required: v => !isEmpty(v),
                              },
                            }
                          }
                        ),
                        ...(
                          type === 'video' && {
                            poster: {
                              label: `サムネイル画像`,
                              type: 'file',
                              inputProps: {
                                accept: `image/*`,
                              },
                            }
                          }
                        ),
                        ...(
                          type === 'pdf' && {
                            text: {
                              label: '表示テキスト',
                              type: 'string',
                            }
                          }
                        ),
                      },
                      onSubmit,
                      submitLabel: '挿入する',
                    }}
                  >
                    {
                      type === 'pdf' ? (
                        <PdfIcon />
                      ) : (
                        <span className={type === 'icon' ? 'fas fa-icons' : `fas fa-file-${type}`} />
                      )
                    }
                  </ModalButton>
                );
              };
            })
          ),
        ]}
        blockStyleFn={getTextAlignClassName}
        {...extraProps}
      />
    </div>
  );
});
